import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function GlobalButlerShortageSalarySurgePage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: '世界的な執事不足により年収13万ドル超えが報告' }
  ];

  const article = {
    category: '業界動向・市場分析',
    date: '2024年10月15日',
    title: '世界的な執事不足により年収13万ドル超えが報告',
    subtitle: 'グローバルな人材不足が示す執事サービスの高い価値',
    heroImage: '/images/generated/jba-fs-074-news_global-butler-shortage-salary-surge.webp',
    lead: '世界的な執事不足により、ヘッドバトラーの年収が13万ドルを超えるまで上昇していることが報告されました。富裕層の都市集中、パンデミック中の不動産購入増加、ビザ手続きの遅延が主な原因とされ、執事サービスへの需要が供給を大きく上回る市場状況が明らかになっています。',
    sections: [
      {
        heading: '執事不足の3つの主要原因',
        image: '/images/generated/jba-fs-075-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: 'ロンドンやニューヨークなど都市部に集中する高級邸宅のイメージ',
        paragraphs: [
          'Robb Reportによると、現在の執事不足の背景には3つの主要な要因があります。第一に、最も裕福な家族がロンドンやニューヨークなどの都市に集中し、これらの地域での需要が急激に増加していることです。',
          '第二に、パンデミック期間中に多くの富裕層が追加の物件を購入し、それに伴ってより多くのスタッフを必要としていることが挙げられます。第三に、特にBrexit後のビザやその他の書類手続きの官僚的な遅延が、人材確保を困難にしています。',
          '特に英国訛りの労働者は、英国に新しく来たアジアや中東の家族、または子供を英国の学校に通わせたい家族の間で高い需要があると報告されています。'
        ]
      },
      {
        heading: '給与の急激な上昇',
        image: '/images/generated/jba-fs-076-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: '希少価値の高まりにより上昇する執事の給与交渉のイメージ',
        paragraphs: [
          '人材不足の結果として、執事の給与は大幅に上昇しています。ヘッドバトラーは現在、年収13万ドル以上を稼げるようになっており、これは執事サービスの専門性と希少価値を明確に示しています。',
          'また、ナニーの年収もCOVID-19以降、約2万6,000ドル増加しており、家庭内サービス全般において人材確保が困難になっていることが分かります。',
          '専門家によると、現在のスタッフは宿泊施設の提供（理想的には専用庭付きの2ベッドルーム一戸建てコテージ）や、家族とペットを連れてくることができる環境が提供されない限り、仕事を検討しないとのことです。'
        ]
      },
      {
        heading: '現代の執事に求められる多様なスキル',
        image: '/images/generated/jba-fs-077-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]',
        alt: '家庭管理から旅行同行、相談相手まで多様な役割を担う現代の執事のイメージ',
        paragraphs: [
          '仕事と労働力の両方が大きく変化しています。現代のバトラーは単に家庭を管理するだけでなく、家族と旅行し、信頼できる相談相手（confidante）、さらにはメンターとしても機能することが求められています。',
          'このような多様なスキルセットを持つ人材の育成には時間とコストがかかり、そのため削減が困難な投資となっています。一方で、若い労働者は仕事が要求する長時間労働を望まない傾向があり、より安価な労働者は必要な訓練とニッチなスキルセットを欠いているという課題があります。',
          '世界最大の執事訓練機関であるBritish Butler Instituteでは、新聞を適切に提示する方法などの伝統的なスキルから、現代的なサービス技術まで幅広い教育を提供しており、このような専門教育の重要性が高まっています。'
        ]
      },
      {
        heading: '英国市場の特殊事情',
        image: '/images/generated/jba-fs-078-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[4/3]',
        alt: '裕福な家族に購入される英国の田舎の邸宅のイメージ',
        paragraphs: [
          'The Timesによると、英国では特に深刻な状況が報告されています。多くの東ヨーロッパの労働者がBrexitとCOVID-19の後に英国を去った一方で、多くの裕福な家族が田舎の邸宅を購入しています。',
          'さらに、上昇する家賃により、邸宅を維持する人材が地域から追い出されているという問題も発生しています。その結果、雇用主は他の購入者がスタッフを引き付けるために近隣のコテージを購入する必要に迫られており、不動産市場にも影響を与えています。'
        ]
      }
    ],
    comment: '世界的な執事不足と給与上昇は、執事という職業の専門性と希少価値が国際的に認識されていることを明確に示す重要な市場シグナルです。現代の執事に求められる多様なスキル—家庭管理から旅行同行、メンター機能まで—は、単なる家事サービスを超えた高度な専門職としての地位を確立しています。\n\n若年層の労働観の変化に対応しながら、質の高い訓練プログラムを通じて次世代の執事人材を育成することが、業界全体の持続的発展にとって不可欠です。日本においても、このグローバルトレンドを踏まえた執事人材の育成と価値向上に積極的に取り組み、国際水準の執事サービスの確立を目指してまいります。',
    source: [
      { label: '出典元', value: 'The Hustle', link: false },
      { label: '記事URL', value: 'https://thehustle.co/news/where-have-all-the-butlers-gone', link: true },
      { label: 'アクセス日', value: '2025年10月', link: false }
    ],
    related: [
      { to: '/news/china-butler-demand-surge', image: '/images/generated/jba-fs-079-news_global-butler-shortage-salary-surge-v4.webp', aspect: 'aspect-[3/4]', alt: '中国富裕層のバトラー需要急増の関連記事', title: '中国富裕層のバトラー需要急増', desc: 'アジア最大市場における執事サービスの急成長' },
      { to: '/news/tiba-global-award-five-years', image: '/images/generated/jba-fs-080-news_global-butler-shortage-salary-surge-v3.webp', aspect: 'aspect-[3/4]', alt: 'TIBA 5年連続グローバルアワード受賞の関連記事', title: 'TIBA 5年連続グローバルアワード受賞', desc: '執事教育の国際的標準と権威性' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'The Hustle', link: false },
    { label: '元記事URL', value: 'https://thehustle.co/news/where-have-all-the-butlers-gone', link: true },
    { label: '給与水準', value: 'https://www.butlerschool.com/home/what-does-a-butler-earn/', link: true },
    { label: '現代執事の待遇', value: 'https://www.forbesglobalproperties.com/storied/the-modern-butler-at-your-service', link: true },
    { label: '給与ガイド2026', value: 'https://www.lighthouse-careers.com/blog/butler-salary-2026-complete-guide-to-private-service-pay-rates/', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-causes', label: '執事不足の3つの主要原因' },
    { id: 's-salary-comp', label: '国際比較でみる執事の給与' },
    { id: 's-surge', label: '給与の急激な上昇' },
    { id: 's-skills', label: '現代の執事に求められる多様なスキル' },
    { id: 's-training', label: '需要増と養成の現場' },
    { id: 's-uk', label: '英国市場の特殊事情' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Market" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '世界的な執事不足により、ヘッドバトラーの年収は13万ドルを超えるまで上昇している',
                '富裕層の都市集中・パンデミック期の物件購入・Brexit後のビザ遅延が主因とされる',
                '現代の執事は家庭管理に加え旅行同行・相談相手・メンターまで担い、養成には時間とコストがかかる'
              ]}
            />

            <SectionHeading id="s-causes" en="Causes">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/shortage-thehustle.jpg"
              alt="執事不足と給与高騰を伝えるThe Hustleの記事のスクリーンショット"
              media="The Hustle"
              url="https://thehustle.co/news/where-have-all-the-butlers-gone"
            />
            <div className="space-y-6">{S[0].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-salary-comp" en="Salary Benchmarks">国際比較でみる執事の給与</SectionHeading>
            <div className="space-y-6">
              <p className={P}>高級人材紹介会社Morgan & Malletによると、トップティアの執事の年収は<Stat>12万〜20万ドル</Stat>に達します。役割が複雑化し需要が供給を上回るなかで、待遇は担い手に有利な水準へと押し上げられています。</p>
              <p className={P}>オランダの養成校The International Butler Academyの資料では、欧州で年<Stat>4万〜7.5万ユーロ</Stat>（手取り・一部10万ユーロ超）、米国平均で約5万4,000ドル、中東で5万〜10万ドル（上限は20万ドル超の例も）とされ、地域と経験による幅が大きいことがわかります。</p>
            </div>
            <SourceShot
              src="/images/news-sources/shortage-butlerschool.jpg"
              alt="執事の給与水準を解説するThe International Butler Academyのページのスクリーンショット"
              media="The International Butler Academy"
              url="https://www.butlerschool.com/home/what-does-a-butler-earn/"
            />

            <SectionHeading id="s-surge" en="Salary Surge">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="ヘッドバトラーの年収は、今や13万ドルを超える。" cite="Robb Report / The Hustle" />
            </div>
            <div className="space-y-6">{S[1].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-skills" en="Modern Skills">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.slice(0, 2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
            <SourceShot
              src="/images/news-sources/shortage-wikipedia.jpg"
              alt="執事という職業の歴史と役割を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（Butler）"
              url="https://en.wikipedia.org/wiki/Butler"
            />
            <div className="space-y-6">{S[2].paragraphs.slice(2).map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-training" en="Demand & Training">需要増と養成の現場</SectionHeading>
            <div className="space-y-6">
              <p className={P}>不足の背景には、新興富裕層（HNWI）の増加と、現代の執事に求められる役割の多角化（セキュリティ・IT・イベント・複数拠点管理）があります。The International Butler Academyでは、ホワイトグローブ・サービスへの応募が2016年から2022年にかけて<Stat>35%</Stat>増加したと報告されています。</p>
              <p className={P}>Guild of Professional English Butlers の会員数も約<Stat>60%</Stat>増えたとされますが、それでも供給は需要に追いつかず、「世界的な執事不足」と評されています。養成校は伝統的な所作から現代的なサービス技術までを教え、その重要性が一段と高まっています。</p>
            </div>

            <SectionHeading id="s-uk" en="UK Market">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">ヘッドバトラー年収</dt><dd className="text-[#333] mt-0.5">13万ドル超</dd></div>
                  <div><dt className="text-[#8f7a56]">トップティア</dt><dd className="text-[#333] mt-0.5">12万〜20万ドル</dd></div>
                  <div><dt className="text-[#8f7a56]">米国平均</dt><dd className="text-[#333] mt-0.5">約5.4万ドル</dd></div>
                  <div><dt className="text-[#8f7a56]">応募増（2016-22）</dt><dd className="text-[#333] mt-0.5">+35%</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
